import React from 'react';
import { Check, Minus } from 'lucide-react';

/**
 * v2 checkbox — cobalt when set, because ticking one is the user's choice, not
 * Robbie's. The native input stays in the DOM (screen-reader visible, keyboard
 * operable, space-toggleable) behind a drawn box; the whole control is wrapped
 * in its label so the text is part of the hit area.
 */
export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'onChange' | 'checked'> {
  checked: boolean;
  /** Some-but-not-all children selected — drawn as a dash, not a tick. */
  indeterminate?: boolean;
  label?: React.ReactNode;
  labelClassName?: string;
  onCheckedChange: (checked: boolean) => void;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  indeterminate = false,
  label,
  labelClassName = 'text-body-sm text-ink',
  className = '',
  disabled,
  onCheckedChange,
  ...rest
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // `indeterminate` is a DOM property with no HTML attribute — it has to be set
  // imperatively for assistive tech to report the mixed state.
  React.useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);

  const filled = checked || indeterminate;

  return (
    <label
      className={`inline-flex select-none items-center gap-s3 ${
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      } ${className}`}
    >
      <input
        {...rest}
        ref={inputRef}
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onCheckedChange(e.target.checked)}
      />
      <span
        aria-hidden="true"
        className={`flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-[4px] border transition-colors duration-100 peer-focus-visible:ring-2 peer-focus-visible:ring-action peer-focus-visible:ring-offset-1 ${
          filled ? 'border-action bg-action text-white' : 'border-line-strong bg-surface text-transparent'
        }`}
      >
        {checked ? (
          <Check className="h-[10px] w-[10px]" strokeWidth={3} />
        ) : indeterminate ? (
          <Minus className="h-[10px] w-[10px]" strokeWidth={3} />
        ) : null}
      </span>
      {label != null && <span className={labelClassName}>{label}</span>}
    </label>
  );
};

export default Checkbox;
